import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Provider,
} from '@angular/core';
import { TmFormElement } from '@thiememeulenhoff/lit-sdk';
import { distinctUntilChanged, ReplaySubject } from 'rxjs';

export function valueAccessorProvider(type: unknown): Provider {
  return {
    provide: NG_VALUE_ACCESSOR,
    // eslint-disable-next-line no-use-before-define
    useExisting: forwardRef(() => type),
    multi: true,
  };
}

@Directive({})
export abstract class BaseValueAccessorDirective<
  ELEMENT extends TmFormElement,
  TYPE,
> implements ControlValueAccessor {
  protected elementRef: ElementRef<ELEMENT> = inject(ElementRef<ELEMENT>);

  protected onChangeFn?: (value: TYPE) => void;

  protected onTouchedFn?: () => void;

  protected valueSubject = new ReplaySubject<TYPE>();

  public value$ = this.valueSubject.asObservable().pipe(distinctUntilChanged());

  @HostListener('input')
  onInput = () => {
    this.valueSubject.next(<TYPE>this.elementRef.nativeElement.value);
    this.onChangeFn?.(<TYPE>this.elementRef.nativeElement.value);
  };

  @HostListener('change')
  onChange = () => {
    this.valueSubject.next(<TYPE>this.elementRef.nativeElement.value);
    this.onChangeFn?.(<TYPE>this.elementRef.nativeElement.value);
  };

  @HostListener('blur')
  onTouched = () => {
    this.onTouchedFn?.();
  };

  writeValue(obj: TYPE): void {
    // @ts-expect-error We know that the value is of the correct type.
    this.elementRef.nativeElement.value = obj;
  }

  registerOnChange(fn: (value: TYPE) => void): void {
    this.onChangeFn = fn;
    this.elementRef.nativeElement.addEventListener('change', this.onChange);
    this.elementRef.nativeElement.addEventListener('input', this.onInput);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
    this.elementRef.nativeElement.addEventListener('blur', this.onTouched);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }
}
